import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sprout, GraduationCap, Mountain, ArrowRight, Clock } from "lucide-react";

const LEVEL_META = {
  starter: { label: "Starter", icon: Sprout, accent: "bg-emerald-100 text-emerald-700", border: "border-emerald-200" },
  intermediate: { label: "Intermediate", icon: GraduationCap, accent: "bg-amber-100 text-amber-700", border: "border-amber-200" },
  advanced: { label: "Advanced", icon: Mountain, accent: "bg-stone-200 text-stone-700", border: "border-stone-300" },
};

export default function Guide() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/guide").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  if (!articles) return <p className="text-stone-500">Loading…</p>;

  const grouped = ["starter", "intermediate", "advanced"].map((lvl) => ({
    level: lvl,
    items: articles.filter((a) => a.level === lvl),
  }));

  return (
    <div className="space-y-12 max-w-4xl" data-testid="guide-page">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Beekeeper's Guide</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">From your first hive to your fiftieth.</h1>
        <p className="text-stone-600 mt-3 max-w-2xl">A field manual we keep updating. Start with the basics, dip into the intermediate when you're ready, and the advanced when you're hooked. Free, forever, for every Hive @ Buzzzillion account.</p>
      </header>

      {grouped.map(({ level, items }) => {
        const m = LEVEL_META[level];
        const Icon = m.icon;
        return (
          <section key={level} data-testid={`guide-section-${level}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-full grid place-items-center ${m.accent}`}><Icon className="w-4 h-4" /></div>
              <h2 className="font-display text-2xl font-medium">{m.label}</h2>
              <span className="text-xs text-stone-500">{items.length} article{items.length === 1 ? "" : "s"}</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {items.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/app/guide/${a.slug}`}
                    className={`block bg-white border ${m.border} hover:border-amber-300 rounded-2xl p-5 transition-colors group`}
                    data-testid={`guide-article-card-${a.slug}`}
                  >
                    <h3 className="font-display text-lg font-medium">{a.title}</h3>
                    <p className="text-sm text-stone-600 mt-1 line-clamp-2">{a.summary}</p>
                    <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.reading_minutes} min read</span>
                      <span className="text-amber-700 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
